import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

import { API_URL } from '../config/api';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      if (!API_URL) {
        throw new Error('Login service is not configured. Please set REACT_APP_API_URL.');
      }

      const payload = {
        action: 'user-login-employee',
        email: formData.email.trim(),
        password: formData.password
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success && result?.employee) {
        const { employee, message } = result;
        // Store required data in localStorage
        // Store essentials (store even if empty string to make keys consistent)
        localStorage.setItem('id', employee.id ?? '');
        localStorage.setItem('name', employee.name ?? '');
        localStorage.setItem('firstName', employee.firstName ?? '');
        localStorage.setItem('middleName', employee.middleName ?? '');
        localStorage.setItem('lastName', employee.lastName ?? '');
        localStorage.setItem('designationName', employee.designationName ?? '');
        localStorage.setItem('status', employee.status ?? '');
        localStorage.setItem('employmentStatus', employee.employmentStatus ?? '');
        localStorage.setItem('email', employee.email ?? '');
        localStorage.setItem('role', 'employee');

        // Additional reporting manager / department info
        localStorage.setItem('isReportingManager', String(!!employee.isReportingManager));
        localStorage.setItem('reportingManagerId', employee.reportingManagerId ?? '');
        localStorage.setItem('reportingManagerName', employee.reportingManagerName ?? '');
        localStorage.setItem('departmentId', employee.departmentId ?? '');
        localStorage.setItem('departmentName', employee.departmentName ?? '');
        toast.success(message || 'Login successful');
        // Navigate based on employmentStatus
        const employmentStatus = employee.employmentStatus?.toUpperCase();
        if (employmentStatus === 'WAITING FOR APPROVAL') {
          navigate('/waiting');
        } else if (employee.employmentStatus === 'pending') {
          navigate('/userdetails');
        } else {
          navigate('/dashboard');
        }
      } else {
        const errorMessage = result?.message || 'Invalid credentials, please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    // <div className="login-container">
    <div>
      <div className="login-wrapper">
        <div className="login-card">
          {/* Left Side - Quotes */}
          <div className="login-left">
            <div className="login-left-content">
              <div className="quote-section">
                <h1 className="quote-title">Welcome to </h1>
                <h1 className="quote-title1">InstaTenders HR Portal!</h1>
                <p className="quote-text">
                  தெய்வத்தான் ஆகாது எனினும் முயற்சிதன் மெய்வருத்தக் கூலி தரும்.
                </p>
                <p className="quote-meaning">
                  Although it be said that, through fate, it cannot be attained, yet labour, with bodily exertion, will yield its reward.
                </p>
                <p className="quote-author">— திருவள்ளுவர் </p>
              </div>

              {/* <div className="quote-features">
                <div className="feature-item">
                  <i className="fas fa-shield-alt"></i>
                  <span>Secure & Reliable</span>
                </div>
                <div className="feature-item">
                  <i className="fas fa-rocket"></i>
                  <span>Fast Performance</span>
                </div>
                <div className="feature-item">
                  <i className="fas fa-users"></i>
                  <span>Team Collaboration</span>
                </div>
              </div> */}
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="login-right">
            <div className="login-body">
              {/* Logo Section */}
              <div className="logo-container-right">
                <img
                  src="/assets/img/Website_logo_color logo-black text-1.png"
                  alt="Company Logo"
                  className="login-logo-right"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div className="logo-placeholder-right" style={{ display: 'none' }}>
                  <i className="fas fa-building"></i>
                </div>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                {/* Email Field */}
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <i className="fas fa-user"></i>
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                      aria-invalid={errors.email ? 'true' : 'false'}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                  </div>
                  {errors.email && (
                    <span className="error-message" id="email-error" role="alert">
                      <i className="fas fa-exclamation-circle"></i> {errors.email}
                    </span>
                  )}
                </div>

                {/* Password Field */}
                <div className="form-group">
                  <label htmlFor="password" className="form-label">
                    Password
                  </label>
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <i className="fas fa-lock"></i>
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      className={`form-input ${errors.password ? 'error' : ''}`}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleChange}
                      autoComplete="current-password"
                      aria-invalid={errors.password ? 'true' : 'false'}
                      aria-describedby={errors.password ? 'password-error' : undefined}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      tabIndex={0}
                    >
                      <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                    </button>
                  </div>
                  {errors.password && (
                    <span className="error-message" id="password-error" role="alert">
                      <i className="fas fa-exclamation-circle"></i> {errors.password}
                    </span>
                  )}
                </div>

                {/* Forgot Password */}
                <div className="form-options">
                  <Link to="/forget" className="forgot-link">
                    Forgot Password
                  </Link>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className={`login-button ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <span>Sign In</span>
                      <i className="fas fa-arrow-right"></i>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
